import type { MessageCallbacks } from './types';
import { normalizeTimestamp } from './types';
import {
  appendTwapPoint,
  appendVwapPoint,
  appendMicropricePoint,
  appendLastPricePoint,
} from '../../store/chartDataSlice';

export function handleMarketStats(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'MARKET_STATS' && obj.secId && obj.stats)) return false;

  const secId = obj.secId.toUpperCase();
  const ts = normalizeTimestamp(obj.timestamp);

  const existingStats = cb.statsBySecIdRef.current.get(secId) ?? { _cumPxSz: 0, _cumSz: 0 };
  const updatedStats = { ...existingStats };
  if (Number.isFinite(obj.stats.vwap)) updatedStats.vwap = Number(obj.stats.vwap);
  if (Number.isFinite(obj.stats.last)) updatedStats.last = Number(obj.stats.last);
  if (Number.isFinite(obj.stats.volume)) updatedStats.volume = Number(obj.stats.volume);
  if (Number.isFinite(obj.stats.open)) updatedStats.open = Number(obj.stats.open);
  if (Number.isFinite(obj.stats.high)) updatedStats.high = Number(obj.stats.high);
  if (Number.isFinite(obj.stats.low)) updatedStats.low = Number(obj.stats.low);
  if (Number.isFinite(obj.stats.close)) updatedStats.close = Number(obj.stats.close);
  // The wire sends camelCase `prevClose`; this read snake_case only, so prevClose was never
  // populated and every "vs previous close" derivation silently fell back. Accept both.
  const prevClose = obj.stats.prevClose ?? obj.stats.prev_close;
  if (Number.isFinite(prevClose)) updatedStats.prevClose = Number(prevClose);
  // priceChange / priceChangePercent arrive on every message and were being discarded outright.
  if (Number.isFinite(obj.stats.priceChange)) updatedStats.priceChange = Number(obj.stats.priceChange);
  if (Number.isFinite(obj.stats.priceChangePercent)) updatedStats.priceChangePercent = Number(obj.stats.priceChangePercent);
  updatedStats.asOf = ts;
  cb.statsBySecIdRef.current.set(secId, updatedStats);

  // Reactive multi-symbol mirror, so a board can render every counter rather than just the
  // selected one. Same reasoning as bookAnalyticsBySecId: the measured MARKET_STATS rate is
  // ~0.35/s across all six counters, far below what a ref is for.
  cb.setStatsBySecId?.(prev => new Map(prev).set(secId, updatedStats));

  if (secId === cb.selectedSymbol) {
    cb.setStats(updatedStats);
    if (Number.isFinite(obj.stats.last)) cb.setCurrentPrice(Number(obj.stats.last));
  }

  if (Number.isFinite(obj.stats.vwap)) {
    cb.chartDispatch(appendVwapPoint({ secId, point: { timestamp: ts, vwap: Number(obj.stats.vwap) } }));
  }

  if (Number.isFinite(obj.stats.last)) {
    cb.chartDispatch(appendLastPricePoint({ secId, point: { timestamp: ts, lastPrice: Number(obj.stats.last) } }));
  }

  cb.lastUpdateBySecIdRef.current.set(secId, ts);
  if (secId === cb.selectedSymbol) cb.setLastUpdateTime(ts);
  return true;
}

export function handleTwap(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'TWAP' && obj.secId && obj.twaps)) return false;

  const secId = obj.secId.toUpperCase();
  const ts = normalizeTimestamp(obj.timestamp);

  const point: import('../../store/chartDataSlice').TwapPoint = { timestamp: ts };
  if (Number.isFinite(obj.twaps['1m']))  point.twap1m  = Number(obj.twaps['1m']);
  if (Number.isFinite(obj.twaps['5m']))  point.twap5m  = Number(obj.twaps['5m']);
  if (Number.isFinite(obj.twaps['15m'])) point.twap15m = Number(obj.twaps['15m']);
  if (Number.isFinite(obj.twaps['30m'])) point.twap30m = Number(obj.twaps['30m']);
  if (Number.isFinite(obj.twaps['1hr'])) point.twap1h  = Number(obj.twaps['1hr']);
  if (Number.isFinite(obj.twaps['4hr'])) point.twap4h  = Number(obj.twaps['4hr']);

  cb.chartDispatch(appendTwapPoint({ secId, point }));

  cb.lastUpdateBySecIdRef.current.set(secId, ts);
  if (secId === cb.selectedSymbol) cb.setLastUpdateTime(ts);
  return true;
}

function storeMicropricePoint(
  secId: string,
  ts: string,
  microprice: number,
  imbalance: number,
  spread: number | undefined,
  bidPrice: number | undefined,
  askPrice: number | undefined,
  cb: MessageCallbacks,
) {
  cb.chartDispatch(appendMicropricePoint({
    secId,
    point: { timestamp: ts, microprice, imbalance, spread, bidPrice, askPrice },
  }));
  cb.lastUpdateBySecIdRef.current.set(secId, ts);
  if (secId === cb.selectedSymbol) cb.setLastUpdateTime(ts);
}

export function handleVwamp(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'VWAMP' && (obj.secId || obj.symbol) && Number.isFinite(obj.vwamp))) return false;

  const secId = (obj.secId || obj.symbol || '').toUpperCase();
  if (!secId) return true;
  const ts = normalizeTimestamp(obj.timestamp);
  storeMicropricePoint(secId, ts, Number(obj.vwamp), Number(obj.imbalance || 0),
    Number.isFinite(obj.spread) ? Number(obj.spread) : undefined,
    Number.isFinite(obj.bidPrice) ? Number(obj.bidPrice) : undefined,
    Number.isFinite(obj.askPrice) ? Number(obj.askPrice) : undefined,
    cb);
  return true;
}

export function handleMicroprice(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'MICROPRICE' && (obj.secId || obj.symbol) && Number.isFinite(obj.microprice))) return false;

  const secId = (obj.secId || obj.symbol || '').toUpperCase();
  if (!secId) return true;
  const ts = normalizeTimestamp(obj.timestamp);
  storeMicropricePoint(secId, ts, Number(obj.microprice), Number(obj.imbalance || 0),
    Number.isFinite(obj.spread) ? Number(obj.spread) : undefined,
    Number.isFinite(obj.bidPrice) ? Number(obj.bidPrice) : undefined,
    Number.isFinite(obj.askPrice) ? Number(obj.askPrice) : undefined,
    cb);
  return true;
}
